import type { LoginResponse } from '../services/api';
import { extractErrorMessage } from './errorHandler';

/** Anzahl der Stellen eines TOTP-Codes. */
export const OTP_LENGTH = 6;

/** Meldung bei HTTP 429 (Rate-Limit) für Login- und Aktivierungs-Versuche. */
export const TOO_MANY_ATTEMPTS_MESSAGE = 'Zu viele Versuche. Bitte in einer Minute erneut versuchen.';

/** Meldung bei einem falschen oder abgelaufenen TOTP-Code. */
export const INVALID_CODE_MESSAGE = 'Ungültiger Code';

/** Schritt des zweistufigen Logins, in dem ein Fehler aufgetreten ist. */
export type LoginStep = 'credentials' | 'two-factor';

/** Ergebnis der Auswertung einer Login-Antwort. */
export type LoginOutcome = 'authenticated' | 'two-factor-required' | 'invalid';

/**
 * Ermittelt, wie auf eine Login-Antwort reagiert werden muss. Eine Antwort ohne
 * Access-Token gilt nie als erfolgreich, damit niemals ein leeres Token gesetzt wird.
 */
export function resolveLoginOutcome(response: Pick<LoginResponse, 'requiresTwoFactor' | 'access_token'>): LoginOutcome {
  if (response.requiresTwoFactor) return 'two-factor-required';
  return response.access_token ? 'authenticated' : 'invalid';
}

/** Liest den HTTP-Statuscode aus einem vom API-Client geworfenen Fehler (sonst `undefined`). */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { statusCode?: unknown }).statusCode;
  return typeof status === 'number' ? status : undefined;
}

/** `true`, wenn `code` aus genau {@link OTP_LENGTH} Ziffern besteht. */
export function isCompleteOtp(code: string): boolean {
  return new RegExp(`^\\d{${OTP_LENGTH}}$`).test(code);
}

/**
 * Übersetzt einen Login-Fehler in eine deutsche Meldung. Im 2FA-Schritt bedeutet 401
 * einen falschen Code; im Zugangsdaten-Schritt bleibt die generische Fehlerbehandlung.
 */
export function mapLoginError(error: unknown, step: LoginStep): string {
  const status = getErrorStatus(error);
  if (status === 429) return TOO_MANY_ATTEMPTS_MESSAGE;
  if (step === 'two-factor' && status === 401) return INVALID_CODE_MESSAGE;
  return extractErrorMessage(error, 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.', 'login');
}

/** Übersetzt einen Fehler beim Bestätigen des Setup-Codes (`2fa/enable`) in eine deutsche Meldung. */
export function mapEnableError(error: unknown): string {
  const status = getErrorStatus(error);
  if (status === 429) return TOO_MANY_ATTEMPTS_MESSAGE;
  if (status === 400) return INVALID_CODE_MESSAGE;
  return extractErrorMessage(error, '2FA konnte nicht aktiviert werden.');
}

/**
 * Entscheidet, ob für einen Admin-Nutzer die Aktion "2FA zurücksetzen" angeboten wird:
 * nur bei aktiver 2FA und nie für das eigene Konto (das Backend lehnt Letzteres mit 403 ab).
 */
export function canResetTwoFactor(
  target: { id: string; twoFactorEnabled: boolean },
  currentUserId: string | undefined,
): boolean {
  return target.twoFactorEnabled && target.id !== currentUserId;
}

/** Formatiert den Aktivierungszeitpunkt für die Anzeige (de-DE); `null` bei fehlendem/ungültigem Wert. */
export function formatEnabledSince(enabledAt: string | undefined): string | null {
  if (!enabledAt) return null;
  const date = new Date(enabledAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
